import axios from "axios";

/**@Constants */
import { base_URL } from "../config/config";

const API_BASE_URL = `${base_URL}/api/paid-vendors`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminAccessToken")}`,
});

/**
 * The admin authorization middleware answers a permission failure with a 200
 * and an `error` flag, so a successful status code is not proof the write
 * happened. Route every response through here before trusting it.
 */
const unwrap = (data) => {
  if (data?.error) {
    throw new Error(data.message || "You do not have permission to do that");
  }
  return data;
};

/** Prefer the API's own message — it is written for the admin, not the console. */
const toError = (error, fallback) =>
  new Error(error?.response?.data?.message || error?.message || fallback);

export const getPaidVendors = async ({
  page = 1,
  limit = 10,
  search = "",
  sortField = "createdAt",
  sortOrder = "desc",
} = {}) => {
  try {
    const { data } = await axios.get(API_BASE_URL, {
      params: { page, limit, sortField, sortOrder, ...(search && { search }) },
      headers: authHeaders(),
    });
    return unwrap(data);
  } catch (error) {
    throw toError(error, "Could not load paid vendors");
  }
};

/** Vendor search that backs the picker. Empty term returns the newest vendors. */
export const lookupVendors = async (search = "", limit = 20) => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/vendor-lookup`, {
      params: { limit, ...(search && { search }) },
      headers: authHeaders(),
    });
    return unwrap(data)?.vendors ?? [];
  } catch (error) {
    throw toError(error, "Could not search vendors");
  }
};

export const addPaidVendors = async (vendorIds) => {
  try {
    const { data } = await axios.post(
      API_BASE_URL,
      { vendorIds },
      { headers: authHeaders() }
    );
    return unwrap(data);
  } catch (error) {
    throw toError(error, "Could not add paid vendors");
  }
};

export const updatePaidVendor = async (vendorId, body) => {
  try {
    const { data } = await axios.put(`${API_BASE_URL}/${vendorId}`, body, {
      headers: authHeaders(),
    });
    return unwrap(data);
  } catch (error) {
    throw toError(error, "Could not update paid vendor");
  }
};

export const removePaidVendor = async (vendorId) => {
  try {
    const { data } = await axios.delete(`${API_BASE_URL}/${vendorId}`, {
      headers: authHeaders(),
    });
    return unwrap(data);
  } catch (error) {
    throw toError(error, "Could not remove paid vendor");
  }
};

export const removePaidVendors = async (vendorIds) => {
  try {
    const { data } = await axios.post(
      `${API_BASE_URL}/multiDelete`,
      { vendorIds },
      { headers: authHeaders() }
    );
    return unwrap(data);
  } catch (error) {
    throw toError(error, "Could not remove paid vendors");
  }
};
